/**
 * Auth API
 *
 * express-session + redis 기반 인증 API
 */

import { apiClient } from '@shared/api/client';
import type { LoginCredentials, RegisterData, User, SessionInfoResponse } from '../types/auth.types';

export const authApi = {
    /**
     * 로그인
     * 성공 시 서버가 세션 쿠키(connect.sid)를 설정
     */
  login: (credentials: LoginCredentials) => {
        const payload = {
            email: (credentials as any).email || (credentials as any).username,
            password: credentials.password,
            browserFingerprint: credentials.browserFingerprint,
        };
        return apiClient.post<{ user: User }>('/auth/login', payload);
    },

    /**
     * 회원가입
     */
    register: (data: RegisterData) => {
        return apiClient.post<{ user: User }>('/auth/register', data);
    },

    /**
     * 로그아웃
     * 서버에서 세션 제거
     */
    logout: () => {
        return apiClient.post('/auth/logout');
    },

    /**
     * 현재 사용자 정보 조회
     * 세션 쿠키로 인증
     * 캐시 방지를 위한 타임스탬프 추가
     */
    getCurrentUser: () => {
        return apiClient.get<{ user: User }>(`/auth/me?_t=${Date.now()}`, {
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache'
            }
        });
    },

    /**
     * 세션 확인
     * 캐시 방지를 위한 타임스탬프 추가
     */
    checkSession: () => {
        return apiClient.get<{ authenticated: boolean; user?: User }>(`/auth/session?_t=${Date.now()}`, {
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache'
            }
        });
    },

    /**
     * 세션 정보 조회
     * 캐시 방지를 위한 타임스탬프 추가
     */
    getSessionInfo: async (): Promise<SessionInfoResponse> => {
        const response = await apiClient.get<any>(`/auth/session-info?_t=${Date.now()}`, {
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache'
            }
        });
        return response as SessionInfoResponse;
    },

    /**
     * 임시 세션 생성
     */
    createTempSession: (customerData: any) => {
        return apiClient.post<{ sessionData: any }>('/auth/temp-session', customerData);
    },

    /**
     * 비밀번호 재설정 링크 요청(이메일 존재 여부 확인 포함)
     */
    requestPasswordReset: (email: string) => {
        return apiClient.post<{ success: boolean; message?: string; code?: string }>(
            '/auth/request-reset',
            { email }
        );
    },

    /**
     * 비밀번호 재설정 토큰 검증
     */
    verifyResetToken: (token: string) => {
        return apiClient.get<{ valid: boolean; message?: string }>(
            '/auth/verify-reset-token',
            {
                params: {
                    token: token
                }
            }
        );
    },

    /**
     * 비밀번호 재설정 제출
     */
    resetPassword: (token: string, newPassword: string) => {
        return apiClient.post<{ success: boolean; message?: string; code?: string }>(
            '/auth/reset-password',
            { token, newPassword }
        );
    },

    /**
     * 로그인 이메일 인증 코드 검증
     */
    verifyLoginEmailCode: (loginToken: string, emailCode: string) => {
        return apiClient.post<{ user: User }>('/auth/login/verify', {
            loginToken,
            emailCode
        });
    },

    /**
     * 로그인 MFA 코드 검증
     */
    verifyLoginMFACode: (loginToken: string, mfaCode: string) => {
        return apiClient.post<{ user: User }>('/auth/login/verify', {
            loginToken,
            mfaCode
        });
    },

    /**
     * 이메일 인증 코드 재발송
     */
    resendLoginEmailCode: (loginToken: string, email: string) => {
        return apiClient.post<{ success: boolean; message?: string; retryCount?: number }>(
            '/auth/login/resend-email',
            { loginToken, email }
        );
    },

    /**
     * 구글 로그인
     */
    loginWithGoogle: (credential: string) => {
        return apiClient.post<{ user: User }>('/auth/login/google', { credential });
    },

    /**
     * MIG 플로우 비밀번호 변경 (세션 기반)
     */
    changePasswordMig: (newPassword: string) => {
        return apiClient.post<{ success: boolean; message?: string }>(
            '/auth/change-password-mig',
            { newPassword }
        );
    },
};
